import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import classNames from 'classnames';
import { Formik, Form } from 'formik';
import { useParams, Link } from 'react-router-dom';
import { branchInitiatedCancelReasons } from '@ourbranch/lookups';
import { format, addDays, parseISO } from 'date-fns';

import warningIcon from 'core/assets/svg/warning.svg';
import infoIcon from 'core/assets/svg/ic-info-dark.svg';
import closeIcon from './close.svg';
import { isInNoChargeWindow, getNoChargeAlertMessage } from './state-rules-utils';
import { ViewSchedule } from 'common/components/payment/view-schedule';
import { FormField } from 'core/components/form';
import { ActionButton } from 'core/components/action-button';
import { Label } from 'core/components/label';
import { Card } from 'core/components/card';
import ValueField from 'core/components/value-field';
import { ElemType, MapClassesToElem } from 'core/helpers/styles-helpers';
import { LottieLoader } from 'core/components/loading';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import { awsDateToDateFormatter } from 'core/helpers/formatters';
import { parseRequestedChanges } from './policy-changes-list/parse-requested-changes';
import { NotificationCard } from 'core/components/notification-card';
import useSession from 'core/hooks/use-session';
import PreviewNotificationCard from './preview-notification-card/preview-notification-card';
import CoverageChange from './signed-documents-required-section/coverage-change';
import SignedDocumentsRequiredSection from './signed-documents-required-section/signed-documents-required-section';
import PolicyChangesList from './policy-changes-list';
import { validationSchema } from './preview.validation';
import { getPriceDetails, parsePreviewChanges, getButtonLabel, getNotes, parseCoverageChanges } from './helpers';
import { track } from 'core/helpers/analytics';
import { ChangesForApproval } from './changes-for-approval';

import styles from './preview.styles';
import { useQueryParams } from 'core/hooks/useQueryParams';

function Preview({
  classes,
  open,
  preview,
  loading,
  onClose,
  onConfirm,
  errors,
  action,
  policyType,
  cancelReason,
  nonRenewReason,
  isAgency,
  changesForApprovalConfig
}) {
  const toast = useToast();
  const { canViewClarionDoorData, canApproveRequestedPIPChanges, user } = useSession();
  const {
    account: {
      policies: {
        policy: {
          billingDetails,
          totalBilled,
          geographicState,
          policy: { effectiveDate },
          policy
        }
      }
    }
  } = useStore();

  const { notesText, cancelBoxTwoText, disableInternalNote } = getNotes({
    action,
    cancelReason,
    nonRenewReason,
    itemsChanged: preview?.itemsChanged,
    diff: preview?.diff
  });

  const showInternalNoteWarning = cancelBoxTwoText && geographicState === 'AZ' && cancelReason === 'CNCN';

  const { arrow, label } = preview ? getPriceDetails(preview, totalBilled) : {};
  const { id: accountId, policyId } = useParams();
  const { previewId: previewIdFromQueryParams } = useQueryParams();

  const hasScheduleChanged = preview && !!preview.nextPayments.length;
  const hasSchedule = preview && (!!preview.nextPayments.length || !!billingDetails.nextPayments.length);

  // Check if we need to show the no charge alert for state rules
  const hasPriceChange = preview && arrow !== null;
  const showNoChargeAlert = !hasPriceChange && preview && isInNoChargeWindow({ geographicState, policy });

  const isRefund = preview?.billingDescription.toLowerCase().includes('refund');
  const hasTrailingDocChanges = preview?.trailingDocChanges?.length > 0;

  const isImmediatePayment =
    preview?.billingDescription.toLowerCase().includes('immediate') ||
    preview?.billingDescription.toLowerCase().includes('today') ||
    preview?.billingDescription.toLowerCase().includes('via check');

  const isChangingToOneTimePay = preview?.diff && JSON.parse(preview.diff)?.full?.addedChanges?.paymentType === 'F';
  const needsEndDateCalculation = branchInitiatedCancelReasons.includes(cancelReason);

  const clarionDoorPriceDiff = () => {
    return (
      canViewClarionDoorData && (
        <Label className={classes.priceDiff}>
          Price difference not what you are expecting?
          <Link
            rel="noreferrer noopener"
            target="_blank"
            to={`/customer/${accountId}/policy/${policyId}/${preview.id}`}
            className={classes.orangeLink}
          >
            &nbsp; View ClarionDoor data.
          </Link>
        </Label>
      )
    );
  };

  const priceDiff = () => {
    return (
      arrow && (
        <>
          <div className={classes.arrow}>
            <img alt="Add segment" src={arrow} className={classes.arrowIcon} />
          </div>

          <Label type="infoInnerTitle" className={classes.priceInfo}>
            Policy price will {label} from ${totalBilled.toFixed(2)} to
            <span className={classes.boldPrice}>${(preview.newTotalPrice || 0).toFixed(2)}</span>
          </Label>
        </>
      )
    );
  };

  const generateEndDateWording = (newEndDate) => {
    if (action === 'reinstate' || action === 'rescind') {
      return (
        <div className={classes.dateInfo}>
          This policy’s last full day of coverage will be
          <span className={classes.boldPrice}>{awsDateToDateFormatter(newEndDate)}</span>
        </div>
      );
    }
    // we dont have cancelEffectiveDate post cancel, so use endDate +1
    return (
      <>
        {newEndDate === effectiveDate && (
          <div className={classes.dateInfo}>
            The policy term will have <span className={classes.boldPrice}>NO COVERAGE</span> and will be cancelled
            effective <span className={classes.boldPrice}>{awsDateToDateFormatter(newEndDate)} at 12:01 AM.</span>
          </div>
        )}

        {newEndDate !== effectiveDate && (
          <div className={classes.dateInfo}>
            This policy’s last full day of coverage is
            <span className={classes.boldPrice}>{awsDateToDateFormatter(newEndDate)}</span> and will be cancelled
            effective
            <span className={classes.boldPrice}>
              {format(addDays(parseISO(newEndDate), 1), 'MM/dd/yyyy')} at 12:01 AM.
            </span>
          </div>
        )}
      </>
    );
  };

  const showErrorToastForChangesThatNeedApproval = (values) => {
    if (changesForApprovalConfig?.hasChangesThatNeedApproval) {
      let showProofOfEligibilityDocumentsError = false;
      let showAllHouseholdResidentsIncludedError = false;
      let showPreAcceptChangesVerificationError = false;

      if (canApproveRequestedPIPChanges) {
        showPreAcceptChangesVerificationError = !values.preAcceptChangesVerification;
      } else {
        showProofOfEligibilityDocumentsError =
          changesForApprovalConfig?.showProofOfEligibilityDocumentsBlock &&
          !values.proofOfElegibilityDocumentsAttestation;

        showAllHouseholdResidentsIncludedError =
          changesForApprovalConfig?.showAllHouseholdResidentsIncludedBlock &&
          !values.allHouseholdResidentsIncludedAttestation;
      }

      if (
        showProofOfEligibilityDocumentsError ||
        showAllHouseholdResidentsIncludedError ||
        showPreAcceptChangesVerificationError
      ) {
        toast.notify({
          type: 'error',
          message: 'Please fix the errors in the form to continue.'
        });
      }
    }
  };

  const isAgentSold = policy.offer.quote.isAgentSold || policy.isAgentSold;

  useEffect(() => {
    if (changesForApprovalConfig?.hasChangesThatNeedApproval) {
      track('Change request - Changes require approval', {
        needProofOfDocuments: changesForApprovalConfig?.showProofOfEligibilityDocumentsBlock,
        requireCheckOfHouseholdResidents: changesForApprovalConfig?.showAllHouseholdResidentsIncludedBlock,
        requireNewPIPForm: changesForApprovalConfig?.showNewPIPFormRequiredBlock,
        isProcessingUser: canApproveRequestedPIPChanges,
        isAgency,
        username: user.username
      });
    }
  }, [changesForApprovalConfig?.hasChangesThatNeedApproval]);

  return (
    <Dialog open={open} scroll="body" classes={MapClassesToElem(ElemType.Modal, classes)}>
      <DialogContent className={classNames([classes.mContent, { [classes.centered]: loading || errors }])}>
        <ActionButton type="edit" icon={closeIcon} className={classes.closeButton} onClick={onClose} />
        {loading && (
          <div className={classes.loadingContainer}>
            <LottieLoader />
            <Label className={classes.previewLoading}>Calculating changes...</Label>
          </div>
        )}
        {preview && !loading && (
          <>
            <Label type="titleSecondary" className={classes.title}>
              {action === 'reinstate' && 'Reinstate Policy Preview'}
              {action === 'rescind' && 'Rescind Policy Cancellation Preview'}
              {action === 'cancel' && `Cancel ${policyType === 'A' ? 'Auto' : 'Home'} Policy Preview`}
              {!action && 'Policy Changes Preview'}
            </Label>
            {priceDiff()}

            {/* Alert for no rate change due to state rules */}
            {showNoChargeAlert && (
              <NotificationCard icon={warningIcon} type="primary">
                <Label variant="body2">{getNoChargeAlertMessage({ geographicState, policy })}</Label>
              </NotificationCard>
            )}

            {['cancel', 'reinstate', 'rescind'].includes(action) && (
              <div className={classes.fields}>
                {generateEndDateWording(preview?.endDate)}
                <div>
                  {action === 'cancel' && needsEndDateCalculation && (
                    <PreviewNotificationCard
                      mainText={`This is the earliest cancelation date the state of ${geographicState} allows based on the selected transaction date of ${awsDateToDateFormatter(
                        preview?.userSelectedEndDate
                      )}.`}
                    />
                  )}

                  {isRefund ? (
                    <PreviewNotificationCard
                      title="This policy requires a refund"
                      mainText={preview.billingDescription}
                      refundWarning={preview.skipImmediateBillOrRefund}
                    />
                  ) : (
                    <ValueField
                      value={
                        <div className={classes.inline}>
                          {preview.billingDescription}{' '}
                          {hasSchedule && preview?.nextPayments?.length > 0 && !isImmediatePayment && (
                            <ViewSchedule
                              inline
                              nextPayments={hasScheduleChanged ? preview.nextPayments : billingDetails.nextPayments}
                            />
                          )}
                        </div>
                      }
                    />
                  )}
                  {isAgentSold && !isAgency && (
                    <PreviewNotificationCard
                      title="Do not attempt to rewrite this policy as Branch Direct or Branch Plus."
                      mainText="This is an agency policy."
                    />
                  )}
                </div>
              </div>
            )}

            {!action && (
              <div className={classes.fields}>
                <Grid className={classes.previewInfo}>
                  <ValueField label="Changes on Coverage" value={parseCoverageChanges(preview.coverageChange)} />
                </Grid>
                <Grid className={classes.previewInfo}>
                  {previewIdFromQueryParams && canApproveRequestedPIPChanges ? (
                    <>
                      <Label type="infoLabel" className="label">
                        The following items were changed
                      </Label>
                      <PolicyChangesList
                        changes={parseRequestedChanges(preview.diff, policy.segments[policy.segments.length - 1])}
                      />
                    </>
                  ) : (
                    <Grid className={classes.previewInfo}>
                      <ValueField
                        label="The following items were changed"
                        value={`${parsePreviewChanges(preview.itemsChanged, preview.itemsChangedAddendum)}`}
                      />
                    </Grid>
                  )}
                </Grid>
                <div>
                  {isRefund ? (
                    <PreviewNotificationCard
                      title={'This policy requires a refund'}
                      mainText={preview.billingDescription}
                      refundWarning={preview.skipImmediateBillOrRefund}
                    />
                  ) : (
                    <ValueField
                      label="Billing Description"
                      value={
                        <div className={classes.inline}>
                          <span
                            // biome-ignore lint/security/noDangerouslySetInnerHtml: Intentional usage
                            dangerouslySetInnerHTML={{ __html: preview.billingDescription }}
                          />{' '}
                          {hasSchedule &&
                            !(isImmediatePayment || isChangingToOneTimePay) &&
                            preview.nextPayments?.length && (
                              <ViewSchedule
                                inline
                                nextPayments={hasScheduleChanged ? preview.nextPayments : billingDetails.nextPayments}
                              />
                            )}
                        </div>
                      }
                    />
                  )}
                </div>
              </div>
            )}
            {clarionDoorPriceDiff()}
            <Formik
              onSubmit={(values) => {
                onConfirm({ ...values, previewId: preview.id, policyPreviewUsername: preview.createdBy.username });
              }}
              validateOnBlur={false}
              validateOnChange={false}
              validateOnMount={false}
              validationSchema={validationSchema(
                isRefund,
                preview?.skipImmediateBillOrRefund,
                preview?.trailingDocChanges,
                isAgency,
                canApproveRequestedPIPChanges && geographicState === 'MI',
                changesForApprovalConfig
              )}
              validate={(values) => {
                // Will only run if there are changes that need approval, really only need this to show error toast when
                // form is submitted with errors
                showErrorToastForChangesThatNeedApproval(values);
              }}
              initialValues={{
                ...preview,
                notes: notesText,
                allowRefund: false,
                signedDocumentsRequiredCheckbox: false,
                policyChangesDescription: '',
                proofOfElegibilityDocumentsAttestation: false,
                allHouseholdResidentsIncludedAttestation: false,
                preAcceptChangesVerification: false
              }}
            >
              <Form>
                {changesForApprovalConfig?.hasChangesThatNeedApproval && geographicState === 'MI' ? (
                  <ChangesForApproval
                    previewId={preview.id}
                    closePolicyPreviewModal={onClose}
                    canApproveRequestedPIPChanges={canApproveRequestedPIPChanges}
                    {...changesForApprovalConfig}
                  />
                ) : (
                  <>
                    {hasTrailingDocChanges && !isAgency && (
                      <Card type="primary" className={classes.coverageChangeContainer}>
                        <Label fontSize={16} type="darkGreenSmallBold">
                          Signed documents are required for this change.
                        </Label>
                        <p>
                          One or more member-signed documents are needed for the following coverage change
                          {preview.trailingDocChanges.length > 1 ? 's' : ''}:
                        </p>
                        {preview.trailingDocChanges.map((trailingDocChange, index) => (
                          <CoverageChange
                            trailingDocChange={trailingDocChange}
                            key={index}
                            preview={preview}
                            hideDownloadButton
                          />
                        ))}
                        <div className={classes.noteContainer}>
                          <img src={infoIcon} alt="info" className={classes.infoIcon} />
                          <div className={classes.noteText}>
                            Please inform the member that they will receive an email signature request after this policy
                            change is processed.
                          </div>
                        </div>
                      </Card>
                    )}
                    {hasTrailingDocChanges && isAgency && <SignedDocumentsRequiredSection preview={preview} />}
                    <Grid container justifyContent="space-around" alignItems="flex-start" spacing={2}>
                      {isRefund && preview?.skipImmediateBillOrRefund && (
                        <FormField
                          id="allowRefund"
                          name="allowRefund"
                          type="checkbox"
                          label="This is an extenuating circumstance, and I want to process the refund manually"
                          mode="light"
                          ignoreGlobalDisabledState
                          xs={12}
                        />
                      )}
                      <FormField
                        id="notes"
                        name="notes"
                        type="string"
                        label="Add an internal note"
                        mode="light"
                        xs={9}
                        ignoreGlobalDisabledState
                        permissions={{ isLicensedAction: false }}
                        disabled={disableInternalNote}
                      />
                      <Button
                        id="submit"
                        name="submit"
                        type="submit"
                        variant="contained"
                        label="Add an internal note"
                        mode="light"
                        xs={3}
                        color="primary"
                        className={classes.submit}
                      >
                        {getButtonLabel(action)}
                      </Button>
                    </Grid>
                  </>
                )}
              </Form>
            </Formik>
            {showInternalNoteWarning && ( // pre-determined notes based on cancel reasons will always show up on docs
              <Label type="statusWarning" className={classes.submitWarning}>
                warning: this internal note will be included on the cancellation form sent to the member
              </Label>
            )}
          </>
        )}
        {errors &&
          errors.map((error) => (
            <Label type="infoValue" key={error} className={classes.errors}>
              {error}
            </Label>
          ))}
      </DialogContent>
    </Dialog>
  );
}

Preview.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  action: PropTypes.oneOf(['cancel', 'reinstate', 'rescind', '']),
  preview: PropTypes.shape({
    coverageChange: PropTypes.string,
    itemsChanged: PropTypes.string,
    itemsChangedAddendum: PropTypes.array,
    billingDescription: PropTypes.string,
    newPremium: PropTypes.number,
    newTotalPrice: PropTypes.number,
    id: PropTypes.string,
    endDate: PropTypes.string,
    nextPayments: PropTypes.array,
    diff: PropTypes.string,
    userSelectedEndDate: PropTypes.string,
    trailingDocChanges: PropTypes.array,
    skipImmediateBillOrRefund: PropTypes.bool
  }),
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string),
  policyType: PropTypes.string.isRequired,
  cancelReason: PropTypes.string,
  nonRenewReason: PropTypes.string,
  isAgency: PropTypes.bool,
  changesForApprovalConfig: PropTypes.shape({
    hasChangesThatNeedApproval: PropTypes.bool,
    showProofOfEligibilityDocumentsBlock: PropTypes.bool
  })
};

Preview.defaultProps = {
  preview: null,
  errors: null,
  action: undefined,
  cancelReason: undefined
};

export default withStyles(styles)(Preview);
